// Wpisy za miesiąc dla szablonu inFaktu: wartości, daty, obliczenia i powód blokady.
// Wspólne dla karty dokumentu i kreatora „Wystaw dokumenty za miesiąc”.
import {
  EVIDENCE_CLASSIFICATION, INFAKT_KIND_LABEL, INTERNAL_INVOICE_KIND, calcLines, money, monthEnd, num2, parseAmount, sumLines, today0, ymd,
  type InfaktLine, type InfaktMonthDoc, type InfaktTemplate, type LineCalc,
} from '@biuro/shared';

export const DONE = ['created', 'found', 'manual'];

export const pct = (s: string) => (/^\d/.test(s) ? `${s.replace('.', ',')}%` : s);

/** Rodzaj dokumentu z dopiskiem: klient faktury albo rodzaj transakcji FW. */
export const templateKind = (t: InfaktTemplate) => [
  INFAKT_KIND_LABEL[t.kind],
  t.kind === 'invoice' && t.clientName ? ` dla ${t.clientName}` : '',
  t.kind === 'internal_invoice' ? `, ${INTERNAL_INVOICE_KIND[t.transactionKind || 'nonaccounting_sales'].toLowerCase()}` : '',
].join('');

export function docPill(doc?: InfaktMonthDoc) {
  if (!doc) return <span className="pill hi">do wystawienia</span>;
  if (doc.status === 'created') return <span className="pill ok">wystawiona{doc.number ? ` ${doc.number}` : ''}</span>;
  if (doc.status === 'found') return <span className="pill ok">jest w inFakcie{doc.number ? `: ${doc.number}` : ''}</span>;
  if (doc.status === 'manual') return <span className="pill ok">wystawiony{doc.number ? `: ${doc.number}` : ''}</span>;
  if (doc.status === 'pending') return <span className="pill warn">w trakcie</span>;
  return <span className="pill bad">do poprawy</span>;
}

/** Co użytkownik wpisuje za miesiąc: wartości pozycji (tekst z pól) i daty dokumentu. */
export interface Entry {
  vals: string[];
  invoiceDate: string;
  saleDate: string;
}

export interface Evaluated {
  values: number[];
  calc: LineCalc[];
  sum: ReturnType<typeof sumLines>;
  /** Dlaczego nie można wystawić z powodu wpisów; pusty, gdy wszystko gra. */
  inputProblem: string;
}

const isVariable = (l: InfaktLine) => !!l.variable && l.variable !== 'none';

export function initialEntry(t: InfaktTemplate, ym: string, doc?: InfaktMonthDoc): Entry {
  const end = monthEnd(ym);
  return {
    vals: t.lines.map((l, i) => {
      const v = doc?.values?.[i];
      if (v != null) return num2(v).replace(/\s/g, '');
      return l.variable === 'quantity' ? String(l.quantity ?? '') : l.variable === 'amount' ? (l.price ? String(l.price).replace('.', ',') : '') : '';
    }),
    // Dokumenty wewnętrzne mają datę ostatniego dnia miesiąca; faktura do kontrahenta – nie wcześniejszą niż dziś.
    invoiceDate: ymd(t.kind === 'invoice' && today0() > end ? today0() : end),
    saleDate: ymd(end),
  };
}

export function evaluateEntry(t: InfaktTemplate, e: Entry): Evaluated {
  // Puste pole to zero (np. brak sprzedaży z daną stawką); nierozpoznany zapis blokuje wystawienie.
  const parsed = e.vals.map((v) => (v.trim() === '' ? 0 : parseAmount(v)));
  const values = parsed.map((v) => v ?? 0);
  const calc = calcLines(t.lines, values);
  const sum = sumLines(calc);
  const varLines = t.lines.filter(isVariable);
  const bad = t.lines.findIndex((l, i) => isVariable(l) && parsed[i] == null);
  const inputProblem = bad >= 0
    ? `Nie rozumiem „${e.vals[bad].trim()}”. Wpisz np. 4000 albo 4 000,00.`
    : !(sum.net > 0) ? (varLines.length && varLines.every((l) => l.variable === 'quantity') ? 'Wpisz ilość za miesiąc.' : 'Wpisz kwotę za miesiąc.') : '';
  return { values, calc, sum, inputProblem };
}

/** Treść POST /api/infakt/issue – dokumenty wewnętrzne mają jedną datę. */
export const issuePayload = (id: string, t: InfaktTemplate, ym: string, e: Entry, values: number[]) =>
  ({ templateId: id, ym, values, invoiceDate: e.invoiceDate, saleDate: t.kind === 'invoice' ? e.saleDate : e.invoiceDate });

/** Pozycje z polami na wartości za miesiąc i podsumowanie kwot. */
export function EntryLines({ t, entry, onVals, disabled }: { t: InfaktTemplate; entry: Entry; onVals: (vals: string[]) => void; disabled?: boolean }) {
  const { calc, sum } = evaluateEntry(t, entry);
  const evidence = t.kind === 'internal_evidence';
  return (
    <>
      <div className="tlines">
        {t.lines.map((l, i) => (
          <div className="tline" key={i}>
            <div className="tline-name">
              <b>{l.name}</b>
              <span className="muted">ryczałt {pct(l.flat)}{evidence ? `, ${EVIDENCE_CLASSIFICATION[t.classification || 'salary'].toLowerCase()}` : `, VAT ${pct(l.vat)}`}</span>
            </div>
            {isVariable(l) ? (
              <label className="tline-in">
                <span>{l.variable === 'quantity' ? `${l.unit || 'ilość'} × ${money(l.price ?? 0)}` : `kwota ${l.priceMode === 'gross' ? 'brutto' : 'netto'} za miesiąc`}</span>
                <input inputMode="decimal" value={entry.vals[i]} disabled={disabled} aria-label={`${l.name}: wartość za miesiąc`}
                  onChange={(ev) => onVals(entry.vals.map((x, j) => (j === i ? ev.target.value : x)))} />
              </label>
            ) : <span className="muted tline-in">stała pozycja</span>}
            <div className="tline-sum">{evidence ? money(calc[i].net) : <>{money(calc[i].net)} netto<br /><span className="muted">{money(calc[i].gross)} brutto</span></>}</div>
          </div>
        ))}
      </div>
      <div className="tcard-total">
        {evidence ? <span>Przychód <b>{money(sum.net)}</b></span> : <>
          <span>Razem netto <b>{money(sum.net)}</b></span>
          <span>VAT <b>{money(sum.vat)}</b></span>
          <span>Brutto <b>{money(sum.gross)}</b></span>
        </>}
        <span className="muted">Ryczałt około <b>{money(sum.flatTax)}</b></span>
      </div>
    </>
  );
}

/** Daty dokumentu: faktura do kontrahenta ma datę wystawienia i sprzedaży, dokumenty wewnętrzne – jedną. */
export function EntryDates({ t, entry, onChange }: { t: InfaktTemplate; entry: Entry; onChange: (patch: Partial<Entry>) => void }) {
  if (t.kind === 'invoice') {
    return (
      <>
        <label className="mini"><span>Data wystawienia</span><input type="date" value={entry.invoiceDate} onChange={(e) => onChange({ invoiceDate: e.target.value })} /></label>
        <label className="mini"><span>Data sprzedaży</span><input type="date" value={entry.saleDate} onChange={(e) => onChange({ saleDate: e.target.value })} /></label>
      </>
    );
  }
  return (
    <label className="mini">
      <span>{t.kind === 'internal_evidence' ? 'Data dowodu' : 'Data wystawienia'}</span>
      <input type="date" value={entry.invoiceDate} onChange={(e) => onChange({ invoiceDate: e.target.value })} />
    </label>
  );
}
